// File: frontend/src/pages/AIAgents/components/VideoModal.js

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import styles from './VideoModal.module.css';

const VideoModal = ({ video, onClose }) => {
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  
  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    // Handle escape key to close
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    
    // Auto-play video when modal opens
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.log('Auto-play prevented:', error);
      });
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className={styles.overlay}>
      <motion.div 
        className={styles.modal}
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
      >
        <div className={styles.header}>
          <h3 className={styles.title}>{video.title}</h3>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close video"
          >
            <X className={styles.closeIcon} />
          </button>
        </div>
        
        <div className={styles.videoContainer}>
          <video 
            ref={videoRef}
            className={styles.video}
            src={video.src}
            poster={video.poster}
            controls
            playsInline
          />
        </div>
      </motion.div>
    </div>
  );
};

VideoModal.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    poster: PropTypes.string.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default VideoModal;